import Quill from 'quill'
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html'
import katex from 'katex'

// Required on window to use quill's built-in formula action
window.katex = katex

// Add classes to the header tags
const Header = Quill.import('formats/header')
class NoviSciHeader extends Header {
  static create (id) {
    const node = super.create(id)
    node.setAttribute('class', `h${id}`)
    return node
  }
}
Quill.register(NoviSciHeader, true)

// Add link class to the anchor tags
const Link = Quill.import('formats/link')
class NoviSciLink extends Link {
  static create (id) {
    const node = super.create(id)
    node.setAttribute('class', 'link')
    return node
  }
}
Quill.register(NoviSciLink, true)

// Add list class to the ol and ul tags
const List = Quill.import('formats/list')
class NoviSciList extends List {
  static create (id) {
    const node = super.create(id)
    node.setAttribute('class', 'list')
    return node
  }
}
Quill.register(NoviSciList, true)

class QuillService {
  constructor () {
    this.olRegExp = /<ol>/i
    this.ulRegExp = /<ul>/i
    this.anchorRegExp = /<a /i
    this.h1RegExp = /<h1>/i
    this.h2RegExp = /<h2>/i
    this.h3RegExp = /<h3>/i
    this.h4RegExp = /<h4>/i
  }

  createQuillInstance (ref, options) {
    const quillInstance = new Quill(ref, options)
    this.addClipboardEnhancements(quillInstance)

    return quillInstance
  }

  addClipboardEnhancements (quillInstance) {
    // Lists from MS Word
    const msWordListTags = [
      'p.MsoListParagraphCxSpFirst', 'p.MsoListParagraphCxSpMiddle', 'p.MsoListParagraphCxSpLast', 'p.MsoListParagraph'
    ]
    msWordListTags.forEach((selector) => {
      quillInstance.clipboard.addMatcher(selector, this.matchMSWordList)
    })
  }

  /**
   * Matcher for Quill to better support lists copied from MS Word
   * Adapted from: https://github.com/quilljs/quill/issues/1225#issuecomment-326044119
   */
  matchMSWordList (node, delta) {
    let ops = delta.ops

    // Trim the front of the first op to remove the bullet/number
    let first = ops[0]
    first.insert = first.insert.trimLeft()
    let firstMatch = first.insert.match(/^(\S+)\s+/)
    if (!firstMatch) return delta
    first.insert = first.insert.substring(firstMatch[0].length, first.insert.length)

    // Trim the newline off the last op
    let last = ops[ops.length - 1]
    last.insert = last.insert.substring(0, last.insert.length - 1)

    // Determine the list type
    let prefix = firstMatch[1]
    let listType = prefix.match(/\S+\./) ? 'ordered' : 'bullet'

    // Add the list attribute
    ops.push({ insert: '\n', attributes: { list: listType } })

    return delta
  }

  convertDeltaToHtml (delta) {
    const converter = new QuillDeltaToHtmlConverter(delta.ops, { multiLineParagraph: false })
    converter.afterRender((groupType, htmlString) => {
      if (groupType === 'list') {
        htmlString = htmlString.replace(this.olRegExp, '<ol class="list">')
        htmlString = htmlString.replace(this.ulRegExp, '<ul class="list">')
      } else if (groupType === 'block') {
        htmlString = htmlString.replace(this.h1RegExp, '<h1 class="h1">')
        htmlString = htmlString.replace(this.h2RegExp, '<h2 class="h2">')
        htmlString = htmlString.replace(this.h3RegExp, '<h3 class="h3">')
        htmlString = htmlString.replace(this.h4RegExp, '<h4 class="h4">')
      } else {
        htmlString = htmlString.replace(this.anchorRegExp, '<a class="link" ')
      }
      return htmlString
    })
    return converter.convert()
  }
}

export const quillService = new QuillService()
